import { useState, useEffect, useCallback } from "react";
import { useClub } from "@/pages/ClubLayout";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Clock, X, Trash2, Sparkles } from "lucide-react";

const AUTH = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("cadii_club_token") || ""}`,
});

export default function ClubTeeTimes() {
  const ctx = useClub();
  const canWrite = ["master", "operador"].includes(ctx?.clubUser?.club_role || "");
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const fetchSlots = useCallback(async () => {
    try {
      const res = await fetch(`${API}/club/tee-slots`, { credentials: "include", headers: AUTH() });
      if (res.ok) setSlots(await res.json());
    } catch {
      toast.error("Error al cargar horarios");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  const handleDelete = async (slotId) => {
    if (!window.confirm("Eliminar este horario?")) return;
    try {
      const res = await fetch(`${API}/club/tee-slots/${slotId}`, {
        method: "DELETE", credentials: "include", headers: AUTH(),
      });
      if (!res.ok) throw new Error();
      toast.success("Horario eliminado");
      fetchSlots();
    } catch { toast.error("Error al eliminar"); }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(`Eliminar TODOS los ${slots.length} horarios? Esta accion no se puede deshacer.`)) return;
    try {
      const res = await fetch(`${API}/club/tee-slots`, {
        method: "DELETE", credentials: "include", headers: AUTH(),
      });
      if (!res.ok) throw new Error();
      toast.success("Todos los horarios eliminados");
      fetchSlots();
    } catch { toast.error("Error al eliminar"); }
  };

  // Group slots by time for display
  const byTime = {};
  slots.forEach((s) => {
    byTime[s.time] = byTime[s.time] || { time: s.time, hole_1: null, hole_10: null };
    if (s.hole === 1) byTime[s.time].hole_1 = s;
    else if (s.hole === 10) byTime[s.time].hole_10 = s;
  });
  const rows = Object.values(byTime).sort((a, b) => a.time.localeCompare(b.time));

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2" data-testid="club-teetimes-title">
            <Clock className="w-6 h-6 text-[#1B4D3E]" strokeWidth={1.5} />
            Horarios del Club
          </h1>
          <p className="text-sm text-[#64748B] mt-1">
            Base de tee times — {ctx?.club?.name}. Al crear una etapa podras elegir cuales usar.
          </p>
        </div>
        {canWrite && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setShowAdd(true)} data-testid="add-slot-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Agregar horario
            </Button>
            <Button onClick={() => setShowWizard(true)} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid="open-wizard-btn">
              <Sparkles className="w-4 h-4 mr-1.5" /> Asistente
            </Button>
            {slots.length > 0 && (
              <Button variant="outline" onClick={handleDeleteAll} className="text-red-600 hover:bg-red-50 border-red-200" data-testid="delete-all-btn">
                <Trash2 className="w-4 h-4 mr-1.5" /> Borrar todos
              </Button>
            )}
          </div>
        )}
      </div>

      {showWizard && (
        <WizardModal onClose={() => setShowWizard(false)} onSaved={() => { setShowWizard(false); fetchSlots(); }} />
      )}

      {showAdd && (
        <AddSlotModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); fetchSlots(); }} />
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <Card className="card-default">
          <CardContent className="py-16 text-center">
            <Clock className="w-12 h-12 text-[#64748B] mx-auto mb-4 opacity-40" />
            <p className="text-[#64748B] font-medium mb-1">No hay horarios configurados</p>
            <p className="text-sm text-[#94A3B8] mb-4">Usa el asistente para generar la base de horarios del club</p>
            {canWrite && (
              <Button onClick={() => setShowWizard(true)} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid="empty-wizard-btn">
                <Sparkles className="w-4 h-4 mr-1.5" /> Abrir asistente
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="card-default">
          <CardContent className="p-0">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <span className="text-sm font-semibold text-[#0F172A]">{slots.length} horarios</span>
              <span className="text-xs text-[#94A3B8]">Hoyo 1 / Hoyo 10</span>
            </div>
            <div className="divide-y divide-gray-50" data-testid="slots-list">
              {rows.map((row) => (
                <div key={row.time} className="px-5 py-2.5 flex items-center justify-between hover:bg-gray-50/50" data-testid={`slot-row-${row.time}`}>
                  <div className="font-bold text-[#0F172A] w-16">{row.time}</div>
                  <div className="flex gap-2 flex-1 justify-end">
                    <HoleChip slot={row.hole_1} hole={1} canWrite={canWrite} onDelete={handleDelete} />
                    <HoleChip slot={row.hole_10} hole={10} canWrite={canWrite} onDelete={handleDelete} />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function HoleChip({ slot, hole, canWrite, onDelete }) {
  if (!slot) {
    return <span className="w-20 text-xs text-[#CBD5E1] text-center py-1">—</span>;
  }
  return (
    <div className="flex items-center gap-1 bg-[#1B4D3E]/10 text-[#1B4D3E] text-xs font-medium px-2.5 py-1 rounded-full" data-testid={`chip-${slot.slot_id}`}>
      <span>H{hole}</span>
      {canWrite && (
        <button
          onClick={() => onDelete(slot.slot_id)}
          className="hover:bg-[#1B4D3E]/20 rounded-full p-0.5 ml-0.5"
          data-testid={`delete-${slot.slot_id}`}
          aria-label="Eliminar"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

// ==========================================================================
// Wizard: bulk-generate
// ==========================================================================

function WizardModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    from_time: "07:00",
    to_time: "10:00",
    interval_minutes: 12,
    holes: [1],
    replace_all: false,
  });
  const [saving, setSaving] = useState(false);

  const toggleHole = (h) => {
    setForm((prev) => ({
      ...prev,
      holes: prev.holes.includes(h) ? prev.holes.filter((x) => x !== h) : [...prev.holes, h].sort(),
    }));
  };

  const handleSave = async () => {
    if (form.holes.length === 0) return toast.error("Selecciona al menos un hoyo");
    setSaving(true);
    try {
      const res = await fetch(`${API}/club/tee-slots/bulk-generate`, {
        method: "POST", credentials: "include", headers: AUTH(),
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error");
      toast.success(`${data.created} horarios generados${data.skipped ? ` (${data.skipped} ya existian)` : ""}`);
      onSaved();
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="wizard-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-bold text-[#0F172A] flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#1B4D3E]" /> Asistente de horarios
          </h3>
          <button onClick={onClose}><X className="w-5 h-5 text-[#64748B]" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-[#64748B]">
            Genera automaticamente los horarios base del club desde una hora inicial hasta una final.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Desde</Label>
              <Input
                type="time"
                value={form.from_time}
                onChange={(e) => setForm({ ...form, from_time: e.target.value })}
                data-testid="wizard-from"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Hasta</Label>
              <Input
                type="time"
                value={form.to_time}
                onChange={(e) => setForm({ ...form, to_time: e.target.value })}
                data-testid="wizard-to"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Intervalo entre salidas</Label>
            <Select value={String(form.interval_minutes)} onValueChange={(v) => setForm({ ...form, interval_minutes: parseInt(v) })}>
              <SelectTrigger data-testid="wizard-interval"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[7, 8, 9, 10, 11, 12, 15, 20].map((m) => (
                  <SelectItem key={m} value={String(m)}>{m} minutos</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Hoyos de salida</Label>
            <div className="flex gap-2" data-testid="wizard-holes">
              {[1, 10].map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => toggleHole(h)}
                  className={`flex-1 px-4 py-2 rounded-lg border-2 font-medium text-sm transition-colors ${
                    form.holes.includes(h)
                      ? "border-[#1B4D3E] bg-[#1B4D3E]/10 text-[#1B4D3E]"
                      : "border-gray-200 text-[#64748B] hover:border-gray-300"
                  }`}
                  data-testid={`wizard-hole-${h}`}
                >
                  Hoyo {h}
                </button>
              ))}
            </div>
            <p className="text-xs text-[#94A3B8] mt-1">
              Selecciona uno o ambos. Si marcas ambos, se crea una salida por cada hoyo en cada horario.
            </p>
          </div>
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <input
              type="checkbox"
              id="replace_all"
              checked={form.replace_all}
              onChange={(e) => setForm({ ...form, replace_all: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-[#1B4D3E]"
              data-testid="wizard-replace"
            />
            <label htmlFor="replace_all" className="text-xs text-amber-800 cursor-pointer">
              Reemplazar TODOS los horarios existentes (si no, se agregan los que no existen)
            </label>
          </div>
        </div>
        <div className="px-6 pb-6 flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancelar</Button>
          <Button className="flex-1 bg-[#1B4D3E] hover:bg-[#163F33] text-white" onClick={handleSave} disabled={saving} data-testid="wizard-save-btn">
            {saving ? "Generando..." : "Generar"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ==========================================================================
// Add single slot
// ==========================================================================

function AddSlotModal({ onClose, onSaved }) {
  const [time, setTime] = useState("07:00");
  const [hole, setHole] = useState(1);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/club/tee-slots`, {
        method: "POST", credentials: "include", headers: AUTH(),
        body: JSON.stringify({ time, hole }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error");
      toast.success("Horario agregado");
      onSaved();
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" data-testid="add-slot-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-bold text-[#0F172A]">Nuevo horario</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-[#64748B]" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Hora</Label>
            <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="add-slot-time" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Hoyo</Label>
            <Select value={String(hole)} onValueChange={(v) => setHole(parseInt(v))}>
              <SelectTrigger data-testid="add-slot-hole"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Hoyo 1</SelectItem>
                <SelectItem value="10">Hoyo 10</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="px-6 pb-6 flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancelar</Button>
          <Button className="flex-1 bg-[#1B4D3E] hover:bg-[#163F33] text-white" onClick={handleSave} disabled={saving} data-testid="add-slot-save-btn">
            {saving ? "Guardando..." : "Agregar"}
          </Button>
        </div>
      </div>
    </div>
  );
}
